import { useTranslation } from 'react-i18next';

import { usePricingContext } from '~/pages/upgrade/_content/usePricingContext';

import CurrencyInput from '~/components/Forms/CurrencyInput';
import Typography from '~/components/Typography';

import type { FontSizes } from '~/interfaces/font_family';
import type { Price } from '~/interfaces/subscriptions';

import currencyFormatter from '../../../utils/currencyFormatter';

interface Props {
  fontFamily: string;
  priceFontSize: FontSizes;
  accentColor: string;
  price: Price;
  txtColor: string;
  isFreePlan: boolean;
  isExternalCheckoutEnabled: boolean;
  isDonationPlan?: boolean;
}

const TierPriceBlock = ({
  fontFamily,
  priceFontSize,
  accentColor,
  price,
  txtColor,
  isFreePlan,
  isExternalCheckoutEnabled,
  isDonationPlan = false,
}: Props) => {
  const isDiscounted = price.amount_cents !== price.original_amount_cents;
  const { donationAmountCents, setDonationAmountCents } = usePricingContext();
  const { t } = useTranslation('', { keyPrefix: 'upgrade' });

  return (
    <div className="block">
      <div className="space-y-1">
        {!isExternalCheckoutEnabled && (
          <Typography
            size="sm"
            weight="bold"
            fontFamily={fontFamily}
            color={accentColor}
            className="capitalize truncate"
          >
            {price.cta}
          </Typography>
        )}

        <div className="flex flex-wrap w-full">
          {isDiscounted && !isDonationPlan && (
            <Typography
              as="h4"
              size={priceFontSize}
              weight="semibold"
              fontFamily={fontFamily}
              color={txtColor}
              className="mr-1 line-through opacity-25"
            >
              {currencyFormatter({ currency: price.currency }).format((price.original_amount_cents || 0) / 100)}
            </Typography>
          )}
          <Typography as="h4" size={priceFontSize} weight="semibold" fontFamily={fontFamily} color={txtColor}>
            <div className="flex flex-row items-center">
              {isDonationPlan ? (
                <>
                  {isExternalCheckoutEnabled ? (
                    <Typography
                      as="span"
                      size="xl"
                      weight="semibold"
                      fontFamily={fontFamily}
                      color={txtColor}
                      className="ml-1"
                    >
                      {t('donation.name_your_price')}
                    </Typography>
                  ) : (
                    <CurrencyInput
                      name={`donation-amount-${price.id}`}
                      value={donationAmountCents}
                      onChange={(value) => setDonationAmountCents(value)}
                      minCents={100}
                      maxCents={100000}
                      currency={price.currency}
                      className="w-full"
                      required
                    />
                  )}
                </>
              ) : (
                <>{currencyFormatter({ currency: price.currency }).format((price.amount_cents || 0) / 100)}</>
              )}
              {!isFreePlan && (
                <Typography
                  as="span"
                  size="xl"
                  weight="semibold"
                  fontFamily={fontFamily}
                  color={txtColor}
                  className="ml-1"
                >
                  /{price.denominator}
                </Typography>
              )}
            </div>
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default TierPriceBlock;
